/******************************************************************************
 *
 * Copyright (c) 2019-2020 ServiceActivator Inc. All Rights Reserved.
 * 
 * Unauthorized copying of the code in this file, completely or partially, 
 * via any medium is strictly prohibited.
 *
 * Proprietary and Confidential.
 * 
 * Written by 'Vishal Goel' <vishal@wakencode.com>, February 2020
 *
 *****************************************************************************/


import {
  makeCoordinatesCompatible,
  makeCoordinatesPostDataCompatible,
  getCenter,
} from "./utlilty";
import uuid from "uniqid";
import find from "lodash/find";
import sortBy from "lodash/sortBy";
import cloneDeep from "lodash/cloneDeep";
import findIndex from "lodash/findIndex";

// distinguish a property workorder by its assigned job type
const processWorkOrder = (workOrder) => {
  let userName = null,
    userNameId = null,
    nickName = null,
    userType = null,
    nickBg = "#F28E1A";

  if (workOrder.technicianUserId) {
    userName = workOrder.technicianUserName;
    userNameId = workOrder.technicianUserId;
    userType = "technician";
    nickBg = "#8fa225";
  } else if (workOrder.contractorId) {
    userName = workOrder.contractorName;
    userNameId = workOrder.contractorId;
    userType = "contractor";
    nickBg = "#F28E1A";
  }
  nickName = getNameInitials(userName);
  return { userName, userNameId, nickName, nickBg, userType };
};

// process selected date properties came from api
export const processSelectedDateProperties = (properties) => {
  let technicianProperties = [];
  let contractorProperties = [];
  let unassignedProperties = [];

  for (let property of properties) {
    // using it to filter property by job type
    let jobsInclude = {
      unassigned_jobs: false,
      technician_jobs: false,
      contractor_jobs: false,
    };

    let mainObj = {
      // custom unique id
      id: uuid(),
      center: makeCoordinatesCompatible(Array(property.centralCoordinates))[0],
      propertyId: property.propertyId,
      propertyName: property.propertyName,
      isPartner: property.isPartner,
      customerName: property.customerName,
      zoom: property.zoomLevel,
      address: property.address
        ? Object.values(property.address).join(", ")
        : "",

      // store property workorders info
      workOrders: [],

      // store property geometries in map api compatible way
      geometries: [],
    };

    for (let g of property.geometries) {
      let compatibleCoordinates = makeCoordinatesCompatible(
        g.listOfCoordinates
      );
      let subObj = {
        gid: uuid(),
        geometryId: g.geometryId,
        center: getCenter(compatibleCoordinates),
        path: compatibleCoordinates,
      };
      mainObj.geometries.push(subObj);
    }

    for (let w of property.workOrders) {
      // if technicianUserId is present then it is technician job
      if (w.technicianUserId) jobsInclude["technician_jobs"] = true;

      // if contractorId is present then it is contractor job
      if (w.contractorId) jobsInclude["contractor_jobs"] = true;

      // if neither contractorId or technicianUserId present then it is unassigned job
      if (!w.technicianUserId && !w.contractorId)
        jobsInclude["unassigned_jobs"] = true;

      let subInfo = processWorkOrder(w);

      let subObj = {
        workOrderId: w.workOrderId,
        workOrderName: w.workOrderName,
        nickName: subInfo.nickName,
        userName: subInfo.userName,
        userNameId: subInfo.userNameId,
        nickBg: subInfo.nickBg,
        userType: subInfo.userType,
      };
      mainObj.workOrders.push(subObj);
    }
    mainObj.jobsInclude = jobsInclude;

    // distinguish properties by job type
    if (jobsInclude["technician_jobs"]) technicianProperties.push(mainObj);
    else if (jobsInclude["contractor_jobs"]) contractorProperties.push(mainObj);
    else unassignedProperties.push(mainObj);
  }

  // sort technicianProperties by username
  let sortedTechnicianProperties = sortBy(technicianProperties, (t) => {
    for (let w of t.workOrders)
      if (w.userType === "technician") return w.userName;
  });

  // sort contractorProperties by username
  let sortedContractorProperties = sortBy(contractorProperties, (t) => {
    for (let w of t.workOrders)
      if (w.userType === "contractor") return w.userName;
  });

  // sort unassignedProperties by property name
  let sortedUnassignedProperties = sortBy(
    unassignedProperties,
    (t) => t.propertyName
  );

  return [
    ...sortedTechnicianProperties,
    ...sortedContractorProperties,
    ...sortedUnassignedProperties,
  ];
};

// process properties when no date is selected
export const processAllProperties = (properties, defaultProperty) => {
  // get propertyId from query params if present
  let params = new URLSearchParams(window.location.search);
  let propertyId = params.get("propertyId");

  /**
   * check if given query params property if already present in state
   * if present we don't want to include it again to avoid duplicate property
   */
  let defaultPropertyId = null;
  if (defaultProperty) defaultPropertyId = defaultProperty.propertyId;

  let processedData = [];

  for (let property of properties) {
    // don't include the already present property
    // this case arise when we need to fetch a single property first given in query params
    if (defaultPropertyId === property.propertyId) continue;

    let mainObj = {
      id: uuid(),
      center: makeCoordinatesCompatible(Array(property.centralCoordinates))[0],
      propertyId: property.propertyId,
      propertyName: property.propertyName,
      isPartner: property.isPartner,
      customerName: property.customerName,
      zoom: property.zoomLevel,
      address: property.address
        ? Object.values(property.address).join(", ")
        : "",
      workOrders: property.workOrders,
      geometries: [],
      // by default select the query params property
      isSelected: propertyId === property.propertyId,
    };

    // store property geometries in map api compatible way
    for (let g of property.geometries) {
      let compatibleCoordinates = makeCoordinatesCompatible(
        g.listOfCoordinates
      );
      let subObj = {
        gid: uuid(),
        geometryId: g.geometryId,
        center: getCenter(compatibleCoordinates),
        path: compatibleCoordinates,
        propertyId: property.propertyId,
      };
      mainObj.geometries.push(subObj);
    }
    processedData.push(mainObj);
  }

  return processedData;
};

// check two polygons paths are equal or not
export const isPathEqual = (updated, unUpdated) => {
  if (updated.length !== unUpdated.length) return false;
  for (let i = 0; i < updated.length; i++) {
    if (updated[i].lat !== unUpdated[i].lat) return false;
    if (updated[i].lng !== unUpdated[i].lng) return false;
  }
  return true;
};

// check if geometry is edited or not
export const isGeometryEdited = (selectedProperty, geofenceData) => {
  if (geofenceData.length < 1 || !selectedProperty) return false;
  if (Object.keys(selectedProperty).length < 1) return false;

  let unUpdatedGeometry = find(
    geofenceData,
    (o) => o.id === selectedProperty.id
  );

  if (!unUpdatedGeometry) return false;

  if (
    unUpdatedGeometry.geometries.length !== selectedProperty.geometries.length
  ) {
    return true;
  }

  for (let g of selectedProperty.geometries) if (g.gid === null) return true;

  for (let updatedGeometry of selectedProperty.geometries) {
    let unUpdatedPath = find(
      unUpdatedGeometry.geometries,
      (o) => o.gid === updatedGeometry.gid
    );
    if (isPathEqual(updatedGeometry.path, unUpdatedPath.path)) continue;
    else return true;
  }
  return false;
};

// sort users by name
export const sortUsers = (users) => {
  let userList = users.filter((user) => user.userId !== null);
  return sortBy(userList, (o) => o.name);
};

// sort contractors by name
export const sortContractors = (contractors) => {
  return sortBy(contractors, (o) => o.name);
};

// set color code of users and contractors
export const setColorCode = (users, contractors) => {
  let colorCode = {};
  for (let u of users)
    colorCode[u.userId] = { colorCode: u.colorCode, photoUrl: u.photoUrl };
  for (let c of contractors)
    colorCode[c.id] = {
      colorCode: c.colorCode,
      photoUrl: null,
    };
  global.userColor = colorCode;
};

// filter property by job type
export const filterByJobType = (geofences, jobType) => {
  if (jobType === "") return geofences;
  return geofences.filter((g) => {
    if (g.jobsInclude[jobType]) return true;
    return false;
  });
};

// filter property by user
export const filterByUserJob = (geofences, user) => {
  return geofences.filter((g) => {
    let isPresent = false;
    for (let w of g.workOrders) {
      isPresent = w.userNameId === user || isPresent;
    }
    return isPresent;
  });
};

// prepare the updated property data for redux store update
export const preparePropertiesForStoreUpdate = (
  properties,
  editedGeometry,
  propertyId
) => {
  let editedIndex = findIndex(properties, (p) => p.propertyId === propertyId);
  if (editedIndex === -1) return;
  let updatedProperties = cloneDeep(properties);
  updatedProperties[editedIndex].geometries = editedGeometry;
  return updatedProperties;
};

// return name initials
export const getNameInitials = (name) => {
  if (!name) return "";
  let nickName;
  let str = "";
  nickName = name.split(" ");
  let count = 0;
  for (let t of nickName) {
    if (t[0]) {
      if (count >= 2) break;
      str += t[0].toUpperCase();
      count++;
    }
  }
  nickName = str;
  return nickName;
};

// prepare post data for newly created / updated geofence
export const prepareGeofencePostData = (geometries) => {
  let postData = {};
  postData.geometries = [];
  for (let g of geometries) {
    postData.geometries.push({
      geometryId: g.geometryId,
      listOfCoordinates: makeCoordinatesPostDataCompatible(g.path),
    });
  }
  return postData;
};
